import React, { useRef, useEffect, useMemo } from "react";
import { MessageBar as FluentMessageBar } from "@fluentui/react-components";

type FluentMessageBarProps = React.ComponentProps<typeof FluentMessageBar>;

const getTextSignature = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === "boolean") {
    return "";
  }
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(getTextSignature).join("|");
  }
  if (React.isValidElement(node)) {
    return getTextSignature(node.props.children);
  }
  return "";
};

/**
 * A wrapper around FluentUI's MessageBar that automatically scrolls into view
 * when it mounts or when its visible text content changes.
 *
 * Drop-in replacement for FluentUI MessageBar — accepts all the same props.
 */
export const MessageBar: React.FC<FluentMessageBarProps> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const textSignature = useMemo(
    () => getTextSignature(props.children),
    [props.children],
  );
  const prevSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const currentSignature = `${props.intent || "default"}:${textSignature}`;
    
    // Skip scroll only if signature is unchanged (not on mount when prev is null)
    if (prevSignatureRef.current !== null && currentSignature === prevSignatureRef.current) {
      return;
    }

    prevSignatureRef.current = currentSignature;

    const timer = setTimeout(() => {
      if (typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [props.intent, textSignature]);

  return (
    <div ref={ref}>
      <FluentMessageBar {...props} />
    </div>
  );
};

export {
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
} from "@fluentui/react-components";
